// "Recently viewed" row (sections/recently-viewed.liquid): the products the shopper opened last,
// newest first. Product pages write the list (assets/predictive-search.js); this file only reads it.
// Each card is the shop's own product card, rendered by Shopify for that product
// (sections/recently-viewed-card.liquid through the Section Rendering API), so price, packs and ADD
// match the other rails. Cards load only when the row nears the screen; the row stays hidden while
// there is nothing to show.
const VIEWED_KEY = 'recently-viewed';
const MAX_SHOWN = 8;
const TIMEOUT_MS = 8000;
const NEAR_PX = 600;

const list = document.querySelector('[data-recently-viewed]');
// The section's wrapper stays shown (only its contents are hidden), so it can be watched.
const section = list?.closest('.shopify-section') || list?.parentElement;

/** Saved handles, newest first. Only URL-safe handles: they go into a request path. */
function readHandles() {
  try {
    const saved = JSON.parse(localStorage.getItem(VIEWED_KEY) || '[]');
    if (!Array.isArray(saved)) return [];
    return saved.filter((handle) => typeof handle === 'string' && /^[\w%-]+$/.test(handle)).slice(0, MAX_SHOWN);
  } catch {
    // Storage blocked or unreadable: nothing to show.
    return [];
  }
}

/** The card's <li>, or null for a product that is gone (404) or a failed request. */
async function fetchCard(handle) {
  const root = window.Shopify?.routes?.root || '/';
  try {
    // A plain GET like a page request: an Accept: application/json header would make Shopify ignore section_id.
    const response = await fetch(`${root}products/${handle}?section_id=recently-viewed-card`, {
      signal: AbortSignal.timeout?.(TIMEOUT_MS),
    });
    if (response.status === 404) return null;
    if (!response.ok) throw new Error(`Recently viewed card failed (${response.status})`);
    const html = new DOMParser().parseFromString(await response.text(), 'text/html');
    return html.querySelector('.rail__item');
  } catch (error) {
    console.error(error);
    return null;
  }
}

/** Shows or hides the heading and the row together. */
function showParts(shown) {
  for (const part of section.querySelectorAll('[data-recently-viewed-part]')) part.hidden = !shown;
}

async function fill(handles) {
  const cards = (await Promise.all(handles.map(fetchCard))).filter(Boolean);
  // Every saved product is gone: give the kept space back.
  if (!cards.length) return showParts(false);
  list.replaceChildren(...cards);
  list.removeAttribute('aria-busy');
  // The cards start at quantity 0; assets/cart.js shows their real quantities.
  document.dispatchEvent(new CustomEvent('products:appended'));
}

const handles = list ? readHandles() : [];
// Saved products: show the heading and keep the row's height at once, while the row is still far below
// the screen, so the footer does not jump when the cards arrive (empty row height: section-home.css).
if (handles.length) showParts(true);
// Load when the row comes near the screen OR has already been scrolled past: a jump to the bottom (End
// key, a fling, a page restored low down) can skip over the row, which an IntersectionObserver never
// reports. One position check per frame while scrolling, and none once loaded.
const nearOrPast = () => section.getBoundingClientRect().top < window.innerHeight + NEAR_PX;
if (handles.length) {
  let ticking = false;
  const check = () => {
    ticking = false;
    if (!nearOrPast()) return;
    window.removeEventListener('scroll', onScroll);
    fill(handles);
  };
  const onScroll = () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(check);
  };
  window.addEventListener('scroll', onScroll, { passive: true });
  check();
}
